import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Grid, Typography, Stack, TextField, MenuItem, Button, LinearProgress, Alert, InputAdornment } from '@mui/material';
import { WarningAmber, Report, FilterList, ChevronRight } from '@mui/icons-material';
import { api } from '../../services/apiClient';
import { formatIndianNumber } from '../../utils/currency';
import { useControlRoomColors } from '../../components/common/controlRoom';
import {
  PageHeader,
  StatCard,
  FilterBar,
  FilterSpacer,
  StatusDot,
  StatusTone,
  TypePill,
  TableEmptyState,
} from '../../components/common/pageKit';

interface ExceptionRow {
  id: number | string;
  kind: string;
  severity: 'critical' | 'warning';
  title: string;
  detail: string;
  impact: number | null;
  age: number;
  owner: string;
  suggestedAction: string;
  route: string;
}

interface ExceptionsData {
  summary?: { critical: number; warning: number; total: number };
  exceptions?: ExceptionRow[];
}

const SEVERITY_TONE: Record<'critical' | 'warning', StatusTone> = {
  critical: 'error',
  warning: 'warning',
};

// A small fixed rotation so kind chips stay visually distinct without the
// backend having to hand us a colour per kind.
const KIND_TINTS = ['#dc2626', '#0284c7', '#7c3aed', '#d97706', '#059669', '#64748b'];

const ProcurementExceptionCenter: React.FC = () => {
  const navigate = useNavigate();
  const C = useControlRoomColors();
  const [d, setD] = useState<ExceptionsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [severityFilter, setSeverityFilter] = useState<'all' | 'critical' | 'warning'>('all');
  const [kindFilter, setKindFilter] = useState<string>('all');

  useEffect(() => {
    setLoading(true);
    api.get<ExceptionsData>('/procurement-analytics/exceptions')
      .then(setD)
      .catch((e: any) => setError(e?.message || 'Failed to load exceptions'))
      .finally(() => setLoading(false));
  }, []);

  const exceptions = d?.exceptions || [];
  const summary = d?.summary || { critical: 0, warning: 0, total: exceptions.length };

  const kinds = useMemo(
    () => Array.from(new Set(exceptions.map((e) => e.kind))).sort(),
    [exceptions],
  );

  const kindTint = (kind: string): string => {
    let hash = 0;
    for (let i = 0; i < kind.length; i += 1) hash = (hash * 31 + kind.charCodeAt(i)) % KIND_TINTS.length;
    return KIND_TINTS[Math.abs(hash) % KIND_TINTS.length];
  };

  const filtered = exceptions.filter((e) => {
    if (severityFilter !== 'all' && e.severity !== severityFilter) return false;
    if (kindFilter !== 'all' && e.kind !== kindFilter) return false;
    return true;
  });

  return (
    <Box>
      <PageHeader
        title="Procurement Exception Center"
        subtitle="Every open finding across requisitions, orders, receipts, invoices and payments — worst first"
      />

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {loading && <LinearProgress sx={{ mb: 2 }} />}

      <Grid container spacing={1.5} sx={{ mb: 2.5 }}>
        <Grid size={{ xs: 12, sm: 4 }}>
          <StatCard title="Total Open" value={summary.total} icon={<Report />} color={C.accent} />
        </Grid>
        <Grid size={{ xs: 6, sm: 4 }}>
          <StatCard title="Critical" value={summary.critical} icon={<WarningAmber />} color={C.bad} />
        </Grid>
        <Grid size={{ xs: 6, sm: 4 }}>
          <StatCard title="Warning" value={summary.warning} icon={<WarningAmber />} color={C.warn} />
        </Grid>
      </Grid>

      <FilterBar>
        <TextField
          select
          size="small"
          label="Severity"
          value={severityFilter}
          onChange={(e) => setSeverityFilter(e.target.value as 'all' | 'critical' | 'warning')}
          sx={{ minWidth: 160 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <FilterList sx={{ fontSize: 18, color: 'text.disabled' }} />
              </InputAdornment>
            ),
          }}
        >
          <MenuItem value="all">All Severities</MenuItem>
          <MenuItem value="critical">Critical</MenuItem>
          <MenuItem value="warning">Warning</MenuItem>
        </TextField>

        <TextField
          select
          size="small"
          label="Kind"
          value={kindFilter}
          onChange={(e) => setKindFilter(e.target.value)}
          sx={{ minWidth: 190 }}
        >
          <MenuItem value="all">All Kinds</MenuItem>
          {kinds.map((k) => (
            <MenuItem key={k} value={k}>{k}</MenuItem>
          ))}
        </TextField>

        <FilterSpacer />

        {(severityFilter !== 'all' || kindFilter !== 'all') && (
          <Button size="small" onClick={() => { setSeverityFilter('all'); setKindFilter('all'); }}>Clear Filters</Button>
        )}
      </FilterBar>

      {!loading && !filtered.length ? (
        <Box sx={{ bgcolor: C.panel, border: `1px solid ${C.line}`, borderRadius: 3, p: 3 }}>
          <TableEmptyState
            title="No open exceptions — procurement is clean."
            hint={exceptions.length ? 'Try clearing the filters above.' : undefined}
          />
        </Box>
      ) : (
        <Stack spacing={1.25}>
          {filtered.map((row) => (
            <Box
              key={row.id}
              sx={{
                display: 'flex', alignItems: 'center', gap: 1.5, p: 1.75, borderRadius: 2,
                bgcolor: C.panel, border: `1px solid ${row.severity === 'critical' ? `${C.bad}55` : C.line}`,
              }}
            >
              <StatusDot label={row.severity === 'critical' ? 'Critical' : 'Warning'} tone={SEVERITY_TONE[row.severity]} />
              <TypePill label={row.kind} tint={kindTint(row.kind)} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontWeight: 700, fontSize: 13.5, color: C.text }} noWrap>{row.title}</Typography>
                <Typography sx={{ fontSize: 12, color: C.sub }} noWrap>{row.detail}</Typography>
              </Box>
              {row.impact !== null && row.impact !== undefined && (
                <Typography sx={{ fontSize: 13, fontWeight: 700, color: C.text, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                  {formatIndianNumber(row.impact)}
                </Typography>
              )}
              <Box
                sx={{
                  px: 1, py: 0.375, borderRadius: 1, fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap',
                  color: C.sub, bgcolor: C.panel2, border: `1px solid ${C.line}`,
                }}
              >
                {row.age}d
              </Box>
              <Typography sx={{ fontSize: 12, color: C.sub, minWidth: 90, textAlign: 'right', whiteSpace: 'nowrap' }} noWrap>
                {row.owner}
              </Typography>
              <Button
                size="small"
                variant="outlined"
                endIcon={<ChevronRight />}
                onClick={() => navigate(row.route)}
                sx={{ textTransform: 'none', whiteSpace: 'nowrap', flexShrink: 0 }}
              >
                {row.suggestedAction}
              </Button>
            </Box>
          ))}
        </Stack>
      )}
    </Box>
  );
};

export default ProcurementExceptionCenter;
